import { useEffect, useState } from "react";
import { apiFetch, ApiError, rateLimitMessage, getSession } from "../../lib/api";
import { lastKickReason, clearLastKickReason } from "../../lib/api";
import { kickCopy } from "../../lib/session-kick-copy";
import { pinLogin, finishWebauthnLogin } from "../../lib/auth";
import { beginDriverPasskeyLogin } from "../../lib/passkey";
import { executeWebAuthnAuthentication } from "../../lib/webauthn";
import { scanTagUid } from "../../lib/nfc";
import { nfcScanErrorMessage } from "../../lib/nfc-login-copy";
// SPEC-72 Phase 2: fire the native cookie flush after every successful login
// (all onLoggedIn sites in this file) so the login Set-Cookie is force-persisted.
import { fireCookieFlush } from "../../lib/cookie-flush";
import { useTenantPhone, phoneHref, type TenantPhoneState } from "../../lib/tenant-phone";
import { TENANT_PHONE } from "./constants";
import Button from "../../components/Button";
import Card from "../../components/Card";

declare const BUILD_TAG: string;

/**
 * SPEC-67 §3.10 → §4: the three device-binding 401 codes mapped to
 * plain-language driver copy. Order of checks in the catch block:
 * pin_locked / invalid_pin → rateLimitMessage (SPEC-66a) → this map →
 * `err.message` fallback. Every code NOT in this map keeps the pre-existing
 * `err.message` path unchanged.
 */
export const DEVICE_LOGIN_ERROR_COPY: Record<string, string> = {
  device_binding_required:
    "This device needs to be linked. Enter your PIN to link it.",
  device_unbound:
    "This device was reset. Enter your PIN to re-link it.",
  device_replay_detected:
    "Your session was replaced. Enter your PIN to sign in again.",
};

/**
 * SPEC-67 §4 helper: `DEVICE_LOGIN_ERROR_COPY[code]` when the error is an
 * ApiError carrying one of the three device codes, else null (the caller
 * keeps its existing `err.message` fallback). Exported for tests.
 */
export function deviceLoginMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  return DEVICE_LOGIN_ERROR_COPY[err.code] ?? null;
}

/**
 * SPEC-68 §4.11 step 0 — the PIN pad must never be reachable-around. Every
 * passkey path is opt-in ON TOP of it, so the probe runs first and any failure
 * falls through to the PIN pad without a network call.
 *
 * `isUserVerifyingPlatformAuthenticatorAvailable` is called with `?.()` and
 * inside a try/catch because it is absent or throws on older WebViews — an
 * unguarded call would reject and abort the handler before the pad rendered.
 */
export async function passkeysUsable(): Promise<boolean> {
  try {
    if (typeof window === "undefined") return false;
    if (!window.PublicKeyCredential) return false;
    if (
      !(await window.PublicKeyCredential
        .isUserVerifyingPlatformAuthenticatorAvailable?.())
    ) {
      return false;
    }
  } catch {
    return false;
  }
  return true;
}

/** SPEC-68 §4.11 — passkey-path copy, mirroring DEVICE_LOGIN_ERROR_COPY. */
export const PASSKEY_LOGIN_ERROR_COPY: Record<string, string> = {
  enrollment_not_available:
    "Passkey setup isn't available right now — use your PIN.",
};

/**
 * SPEC-68 §4.11 helper: `PASSKEY_LOGIN_ERROR_COPY[code]` when the error is an
 * ApiError carrying a passkey code, else null (the caller keeps its existing
 * `err.message` fallback). Exported for tests. Does NOT touch the SPEC-67
 * device map — the two chains stay independent.
 */
export function passkeyLoginMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  return PASSKEY_LOGIN_ERROR_COPY[err.code] ?? null;
}

interface RosterEntry {
  userId: string;
  name: string;
}

export default function DriverLogin({
  onLoggedIn,
}: {
  /** SINGLE-SCAN-MORNINGS (C): the 2nd arg carries the tag UID scanned at
   *  the login gate, or undefined when this login never scanned (the SPEC-71
   *  §3B bound-device bypass and passkey-first paths). DriverApp arms the
   *  punch handoff from it so CLOCK IN can skip its second scan. */
  onLoggedIn: (driverId: string, loginScanUid?: string | null) => void;
}) {
  // Tri-state phone:
  //  - loading  : first paint. Consumer paints the constant fallback so
  //    the lockout screen is never empty in a dead zone.
  //  - ok + s   : the live office phone (link it).
  //  - ok + null: the office cleared the phone. Show a disabled "not
  //    configured" state — NO `tel:` link.
  //  - fallback : fetch failed. Paint the constant + link it.
  const phoneState: TenantPhoneState = useTenantPhone();

  const [roster, setRoster] = useState<RosterEntry[] | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);

  const [selected, setSelected] = useState<RosterEntry | null>(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
  const [lockedOut, setLockedOut] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // SPEC-68 §4.11 — set when a passkey attempt was skipped/failed so the PIN
  // pad can offer the "Use passkey instead" retry. A note, never a blocker.
  const [passkeyFallbackNotice, setPasskeyFallbackNotice] = useState(false);

  // SPEC-71 §3C — the durable kick backstop. A DriverApp epoch remount
  // (`key={epoch}`) wipes the surface-local toast stack, so the machine reason
  // is read here ONCE (lazy initializer captures the first render's value) and
  // the module var is cleared in the mount effect — "displayed once".
  const [kickReason] = useState<string | null>(() => lastKickReason());
  useEffect(() => {
    clearLastKickReason();
  }, []);

  // Load the driver roster (public endpoint — names + userIds only).
  useEffect(() => {
    apiFetch<RosterEntry[]>("/api/v1/auth/driver-roster")
      .then(setRoster)
      .catch((e) =>
        setRosterError(e instanceof Error ? e.message : String(e)),
      );
  }, []);

  // Auto-submit once the PIN has 4 digits.
  useEffect(() => {
    if (pin.length === 4 && selected && !submitting && !lockedOut) {
      void submit(selected.userId, pin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  /**
   * SPEC-70 §2.8 — the ONE scanner for every login-path scan (r4 #42).
   * SPEC-71 §3C (BUG #3) removed the proactive PIN-path caller; the two
   * remaining callers are both REACTIVE: submit's 401 nfc_login_required
   * branch and selectDriver's reactive branch. Resolves the normalized UID,
   * or null when the scan failed — the caller stays on its surface, and the
   * failure copy is ALREADY surfaced inline here (never thrown).
   */
  const scanForLogin = async (): Promise<string | null> => {
    const result = await scanTagUid();
    if ("uid" in result) return result.uid;
    setError(nfcScanErrorMessage(result.error));
    return null;
  };

  const submit = async (userId: string, currentPin: string) => {
    setSubmitting(true);
    setError(null);
    try {
      // SPEC-71 §3C (BUG #3) — reactive-primary inversion. Submit the PIN
      // WITHOUT a UID first: a bound device is answered 200 by the server's
      // §3B bypass (presence was proven at bind time), so the road-strand case
      // costs zero scans. Only an UNBOUND device gets the 401
      // nfc_login_required below and enters the reactive scan-and-resubmit
      // branch (r4 #36: captured userId/currentPin + the scanned UID). The old
      // proactive scan-and-block starved the bypass — the PIN never reached the
      // server, so the bound-device answer never arrived.
      const { driverId } = await pinLogin(userId, currentPin);
      // SPEC-72 Phase 2: force-persist the login Set-Cookie (fire-and-forget,
      // best-effort) before the surface hand-off.
      fireCookieFlush();
      onLoggedIn(driverId, null);
    } catch (err) {
      // SPEC-70 §2.8 — the reactive recovery for a stale config cache: a 401
      // nfc_login_required (gate flipped ON after a cached probe said OFF)
      // gets ONE scan + resubmit. The resubmit uses the handler's CAPTURED
      // (userId, currentPin) — never the `pin` state, which this handler has
      // already cleared to "" (r4 #36, SPEC-62 stale-state class). EVERY other
      // code keeps the pre-existing sequence verbatim (49d-family pins).
      if (err instanceof ApiError && err.code === "nfc_login_required") {
        const uid = await scanForLogin();
        if (uid === null) {
          // The scan copy is already on the pad; clear the digits so the pad
          // is re-enterable and stay.
          setPin("");
          return;
        }
        try {
          // Resubmit DIRECTLY (no recursion — the proactive probe must not
          // re-run, which would prompt a second scan) with the CAPTURED
          // (userId, currentPin) — never the `pin` state (r4 #36).
          const { driverId } = await pinLogin(userId, currentPin, uid);
          // SPEC-72 Phase 2: force-persist the login Set-Cookie (fire-and-
          // forget, best-effort) before the surface hand-off.
          fireCookieFlush();
          onLoggedIn(driverId, uid);
        } catch (resubmitErr) {
          // A second nfc_login_required (unregistered/deactivated tag) must
          // NOT loop: surface and stop. SPEC-66a's 429-first contract holds
          // on the resubmit too (the nfcfail bucket can 429 here).
          const rl = rateLimitMessage(resubmitErr);
          setError(
            rl ??
              (resubmitErr instanceof Error
                ? resubmitErr.message
                : String(resubmitErr)),
          );
          setPin("");
        }
        return;
      }
      if (err instanceof ApiError) {
        if (err.code === "pin_locked") {
          setLockedOut(true);
          setError(null);
        } else if (err.code === "invalid_pin") {
          const remaining = err.body?.attemptsRemaining;
          if (typeof remaining === "number") {
            if (remaining <= 0) setLockedOut(true);
            else setAttemptsLeft(remaining);
          }
          setError("Incorrect PIN. Try again.");
        } else {
          // SPEC-66a: a 429 rate_limited carries the wait time — surface it
          // instead of the transport default. Every other ApiError code keeps
          // the pre-existing `err.message` fallback.
          const rl = rateLimitMessage(err);
          // SPEC-67 §4: device-binding codes map to their own copy BEFORE the
          // generic fallback; all other codes still fall through unchanged.
          const device = deviceLoginMessage(err);
          // SPEC-68 §4.11: passkey-path codes join the chain after the device
          // map and before the generic fallback.
          const passkey = passkeyLoginMessage(err);
          setError(rl ?? device ?? passkey ?? err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setPin("");
    } finally {
      setSubmitting(false);
    }
  };

  // SPEC-68 §4.11 — passkey-FIRST tap. Every failure mode lands on the PIN
  // pad: the probe short-circuits before any network call, an unavailable
  // begin returns the pad with no nag, and ANY thrown error (cancelled prompt,
  // offline, an un-deployed Worker's 404) sets the fallback note and shows the
  // pad. The pad is the guaranteed path; nothing above may skip it.
  const selectDriver = async (entry: RosterEntry) => {
    setSelected(entry);
    setPin("");
    setError(null);
    setAttemptsLeft(null);
    setLockedOut(false);
    setPasskeyFallbackNotice(false);

    // No network call at all when the device cannot do platform passkeys.
    if (!(await passkeysUsable())) return;

    try {
      const begin = await beginDriverPasskeyLogin(entry.userId);
      if (!begin.available || !begin.challengeToken || !begin.options) {
        // No passkey on file: the PIN pad, with NO nag. This is the normal
        // path for most drivers and must look exactly like today.
        return;
      }
      const assertion = await executeWebAuthnAuthentication(begin.options as never);
      const user = await finishWebauthnLogin(begin.challengeToken, assertion as never);
      // SPEC-72 Phase 2: force-persist the login Set-Cookie (fire-and-forget,
      // best-effort) before the surface hand-off.
      fireCookieFlush();
      // finishWebauthnLogin folds body.driverId onto the session user
      // (lib/auth.ts), so the session is the single source here.
      onLoggedIn(
        getSession()?.user?.driverId ??
          (user as { driverId?: string }).driverId ??
          "",
        null,
      );
    } catch (err) {
      // SPEC-70 §2.8 (r3 #25) — the passkey auto-path's reactive recovery. A
      // 401 nfc_login_required means the driver HAS a credential and the gate
      // wants the tag: scan (reusing the PIN path's scanner) and retry begin
      // with the UID so the ceremony continues. Discriminated FIRST — every
      // other failure keeps the pre-existing sequence VERBATIM below
      // (setPasskeyFallbackNotice / setError(null) — SPEC-68 pins 49d/49d-2).
      if (err instanceof ApiError && err.code === "nfc_login_required") {
        const uid = await scanForLogin();
        if (uid === null) return; // scan copy already on the pad; stay there
        try {
          const begin = await beginDriverPasskeyLogin(entry.userId, uid);
          if (!begin.available || !begin.challengeToken || !begin.options) {
            // The retry said "no passkey on file" — the pad, with NO nag
            // (mirrors the 49d-2 unavailable-begin semantics).
            return;
          }
          const assertion = await executeWebAuthnAuthentication(begin.options as never);
          const user = await finishWebauthnLogin(begin.challengeToken, assertion as never);
          // SPEC-72 Phase 2: force-persist the login Set-Cookie (fire-and-
          // forget, best-effort) before the surface hand-off.
          fireCookieFlush();
          onLoggedIn(
            getSession()?.user?.driverId ??
              (user as { driverId?: string }).driverId ??
              "",
            uid,
          );
          return;
        } catch {
          // The recovery ceremony itself failed (offline begin, cancelled
          // prompt, a second nfc_login_required for an unregistered tag) —
          // same contract as the original attempt: fall through to the
          // fallback note below rather than dying silently.
        }
      }
      // ANY other failure lands the driver on the PIN pad with the
      // shorter-session note. A cancelled prompt, an offline begin, or a 404
      // from an un-deployed Worker all read as "no passkeys here", never as a
      // broken login screen — the PIN pad is the guaranteed path.
      setPasskeyFallbackNotice(true);
      setError(null);
    }
  };

  const tapDigit = (d: string) => {
    if (lockedOut) return;
    if (pin.length < 4) setPin((p) => p + d);
  };

  const backspace = () => setPin((p) => p.slice(0, -1));

  const backToRoster = () => {
    setSelected(null);
    setPin("");
    setError(null);
    setAttemptsLeft(null);
    setLockedOut(false);
  };

  if (rosterError) {
    return (
      <main className="driver-login">
        <Card>
          <img
          className="title-logo"
          src="/eagle-dispatch-logo.jpg"
          alt="Eagle Dispatch"
        />
          <p className="err">Failed to load drivers.</p>
          <p className="muted">{rosterError}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </Card>
        <p
          className="muted build-stamp"
          style={{ textAlign: "center", marginTop: 12 }}
        >
          build {BUILD_TAG.slice(0, 7)} · {BUILD_TAG.slice(8, 18)} UTC
        </p>
      </main>
    );
  }

  if (!roster) {
    return (
      <main className="driver-login">
        <p className="muted">Loading…</p>
      </main>
    );
  }

  // ── Lockout message (3 strikes, or API already returned pin_locked) ──────
  if (lockedOut) {
    // Resolve the tri-state to a renderable block. All three branches
    // share the same Card chrome; only the link target + text differ.
    // - ok+string  → link with live number
    // - ok+null    → disabled "not configured" state (no tel link)
    // - fallback   → link with the constant
    // - loading    → same as fallback (constant rendered, link active)
    // The string `phoneText` is always a non-null, real string; the
    // `phoneLinkHref` is a real `tel:` URL only when the office is
    // configured. The "not configured" branch intentionally has no link
    // so a cleared office field produces a non-actionable screen, NOT
    // a fabricated fallback.
    const phoneText =
      phoneState.status === "ok" && phoneState.phone !== null
        ? phoneState.phone
        : TENANT_PHONE; // loading OR fallback OR ok+null
    // Send-back re-verification fix (Supervisor): loading AND fallback must
    // render the tappable constant — an offline driver on the PIN-lockout
    // screen needs the fallback number, not a dead span. Only ok+null (the
    // office explicitly cleared the phone) disables the link. Mirrors
    // WaitingScreen's three-branch structure.
    const phoneLinkHref =
      phoneState.status === "ok"
        ? phoneState.phone !== null
          ? phoneHref(phoneState.phone)
          : null
        : phoneHref(TENANT_PHONE); // loading OR fallback → constant link

    return (
      <main className="driver-login">
        <Card>
          <img
          className="title-logo"
          src="/eagle-dispatch-logo.jpg"
          alt="Eagle Dispatch"
        />
          <h2>PIN Locked</h2>
          <p className="muted">Forgot PIN? Call Office</p>
          {phoneLinkHref ? (
            <a className="tel-button" href={phoneLinkHref}>
              📞 {phoneText}
            </a>
          ) : (
            <span className="tel-button tel-button--disabled" aria-disabled="true">
              Office phone not configured
            </span>
          )}
          <p className="muted footnote">
            The office can reset your PIN for you.
          </p>
          <Button variant="ghost" onClick={backToRoster}>
            ← Back
          </Button>
        </Card>
      </main>
    );
  }

  // ── PIN pad (a name is selected) ─────────────────────────────────────────
  if (selected) {
    return (
      <main className="driver-login">
        <Card>
          <img
          className="title-logo"
          src="/eagle-dispatch-logo.jpg"
          alt="Eagle Dispatch"
        />
          <p className="sub">Hi {selected.name} — enter your PIN</p>

          {passkeyFallbackNotice && (
            <p className="muted" style={{ fontSize: "13px" }}>
              Using PIN — you'll be signed out sooner.{" "}
              <button
                type="button"
                className="link-button"
                onClick={() => void selectDriver(selected)}
              >
                Use passkey instead
              </button>
            </p>
          )}

          <div className="pin-display" aria-label="PIN">
            {"••••".split("").map((_, i) => (
              <span
                key={i}
                className={`pin-dot ${i < pin.length ? "pin-dot--filled" : ""}`}
              />
            ))}
          </div>

          {error && <p className="form-error">{error}</p>}
          {attemptsLeft !== null && attemptsLeft > 0 && !error && (
            <p className="muted">Attempts remaining: {attemptsLeft}</p>
          )}

          <div className="pin-pad">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
              <button
                key={d}
                type="button"
                className="pin-key"
                onClick={() => tapDigit(d)}
              >
                {d}
              </button>
            ))}
            <span />
            <button
              type="button"
              className="pin-key"
              onClick={() => tapDigit("0")}
            >
              0
            </button>
            <button
              type="button"
              className="pin-key pin-key--back"
              onClick={backspace}
              aria-label="Delete digit"
            >
              ⌫
            </button>
          </div>

          {submitting && <p className="muted">Checking…</p>}
          <Button variant="ghost" onClick={backToRoster}>
            ← Choose another name
          </Button>
        </Card>
      </main>
    );
  }

  // ── Name grid ────────────────────────────────────────────────────────────
  return (
    <main className="driver-login">
      <Card>
        <img
          className="title-logo"
          src="/eagle-dispatch-logo.jpg"
          alt="Eagle Dispatch"
        />
        <p className="sub">Select your name</p>
        {kickReason && (
          <p className="muted kick-reason" style={{ fontSize: "13px" }}>
            {kickCopy(kickReason)}
          </p>
        )}
        <div className="name-grid">
          {roster.map((entry) => (
            <button
              key={entry.userId}
              type="button"
              className="name-button"
              onClick={() => selectDriver(entry)}
            >
              {entry.name.split(" ")[0]}
            </button>
          ))}
        </div>
      </Card>
      <a
        className="dispatch-login-pill"
        href="/dispatch"
      >
        Dispatch Login
      </a>
      <p
        className="muted build-stamp"
        style={{ textAlign: "center", marginTop: 12 }}
      >
        build {BUILD_TAG.slice(0, 7)} · {BUILD_TAG.slice(8, 18)} UTC
      </p>
    </main>
  );
}
